import {
  customEvent,
  setHtmlScrollable,
  setHtmlUnscrollable
} from '@jspartials/general-utils';
import { gtmCustomEvent, pushOnDataLayer } from '@jspartials/gtm-utils';
import { getCookie } from '@jspartials/cookie-utils';
import {
  createElement,
  appendElementContent,
  prepend,
  listenDOMEvent
} from '@jspartials/dom-api-utils';

const starsIcon =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 22V17M4.5 7V2M2 4.5H7M2 19.5H7M13 3L11.2658 7.50886C10.9838 8.24209 10.8428 8.60871 10.6235 8.91709C10.4292 9.1904 10.1904 9.42919 9.91709 9.62353C9.60871 9.8428 9.24209 9.98381 8.50886 10.2658L4 12L8.50886 13.7342C9.24209 14.0162 9.60871 14.1572 9.91709 14.3765C10.1904 14.5708 10.4292 14.8096 10.6235 15.0829C10.8428 15.3913 10.9838 15.7579 11.2658 16.4911L13 21L14.7342 16.4911C15.0162 15.7579 15.1572 15.3913 15.3765 15.0829C15.5708 14.8096 15.8096 14.5708 16.0829 14.3765C16.3913 14.1572 16.7579 14.0162 17.4911 13.7342L22 12L17.4911 10.2658C16.7579 9.98381 16.3913 9.8428 16.0829 9.62353C15.8096 9.42919 15.5708 9.1904 15.3765 8.91709C15.1572 8.60871 15.0162 8.24209 14.7342 7.50886L13 3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Selectors
const selector = {
  pdp: {
    wrapper: '[data-action="Product-Show"]',
    buttonAddToCart: '.js-button-add-to-cart'
  },
  productTileVariant: '.js-product-color-variants__list-item',
  menuDesktop: '.js-menu-desktop__header',
  popup: {
    backdrop: '.js-relevancer-popup-backdrop',
    button: '.js-relevancer-popup-button',
    close: '.js-relevancer-popup-close'
  },
  cartIcon: '.js-mini-cart-icon',
  buttonLinkHeader: '.js-relevancer-button-link-header',
  iconLinkHeader: '.js-relevancer-icon-link-header'
};

// Elements
const element = {
  menuDesktop: document.querySelector(selector.menuDesktop) || null,
  cartIcon: document.querySelector(selector.cartIcon) || null
};

// Variables
const text = {
  buttonLinkHeader: {
    es: 'Tu selección',
    en: 'Your selection',
    fr: 'Votre sélection',
    it: 'La tua selezione',
    pt: 'Sua seleção',
    de: 'Ihre Auswahl',
    el: 'Η επιλογή σας'
  },
  popup: {
    title: {
      es: 'Tu selección personalizada',
      en: 'Your personalized selection',
      fr: 'Votre sélection personnalisée',
      it: 'La tua selezione personalizzata',
      pt: 'Sua seleção personalizada',
      de: 'Ihre personalisierte Auswahl',
      el: 'Η εξατομικευμένη επιλογή σας'
    },
    description: {
      es: 'Hemos preparado una selección de gafas perfectas para ti, basándonos en tu actividad en nuestra web.',
      en: 'We have prepared a selection of glasses perfect for you, based on your activity on our website.',
      fr: 'Nous avons préparé une sélection de lunettes parfaites pour vous, basée sur votre activité sur notre site web.',
      it: 'Abbiamo preparato una selezione di occhiali perfetti per te, basata sulla tua attività sul nostro sito web.',
      pt: 'Preparamos uma seleção de óculos perfeitos para você, com base na sua atividade em nosso site.',
      de: 'Wir haben eine Auswahl an Brillen für Sie zusammengestellt, die auf Ihrer Aktivität auf unserer Website basiert.',
      el: 'Έχουμε ετοιμάσει μια επιλογή γυαλιών τέλεια για εσάς, βασισμένη στη δραστηριότητά σας στον ιστότοπό μας.'
    },
    button: {
      es: 'Ver mi selección',
      en: 'See my selection',
      fr: 'Voir ma sélection',
      it: 'Vedi la mia selezione',
      pt: 'Ver minha seleção',
      de: 'Meine Auswahl ansehen',
      el: 'Δείτε την επιλογή μου'
    }
  }
};
const localeCookieName = 'HW_preferred_locale';
const testAbCookieName = 'e_seg';
const testAbSegmentName = 'B';
const maxEventsToStore = 500;
const localStorageName = {
  events: 'relevancerEvents',
  popupClosed: 'relevancerPopupClosed'
};
const localStorageValueForPopupClosed = 'yes';
const eventName = {
  pdpView: 'pdp',
  addToCart: 'cart',
  productVariantClick: 'plp'
};
const eventScore = {
  pdpView: 10,
  addToCart: 10,
  productVariantClick: 1
};
const scoreThreshold = 50;
const listOfProductTileVariantsViewed = [];
let popupOpened = false;

// Functions

/**
 * Retrieves the list of events from local storage
 * @returns {Array} - Returns an array of events stored in local storage
 */
const getEventsListFromLocalStorage = () => {
  return JSON.parse(localStorage.getItem(localStorageName.events)) || [];
};

/**
 * Retrieves the popup closed status from local storage
 * @returns {string} - Returns the popup closed status from local storage, or an empty string if not set
 */
const getPopupClosedFromLocalStorage = () => {
  return localStorage.getItem(localStorageName.popupClosed) || '';
};

/**
 * Adds an event to local storage
 * @param {object} data - The event data to be stored
 * @returns {void}
 */
const addEventToLocalStorage = (data) => {
  const currentEventsList = getEventsListFromLocalStorage();

  data.ts = Math.floor(Date.now() / 1000);
  currentEventsList.push(data);
  localStorage.setItem(
    localStorageName.events,
    JSON.stringify(currentEventsList)
  );
  checkLocalStorageForEvents();
};

/**
 * Checks if the customer belongs to a specific segment based on a cookie
 * @returns {boolean} - Returns true if the customer belongs to the segment, otherwise false
 */
const customerBelongsToSegment = () => {
  const testCookieValue = getCookie(testAbCookieName);
  return testCookieValue === testAbSegmentName;
};

/**
 * Checks if the product detail page is loaded
 * @returns {boolean} - Returns true if the PDP is loaded, otherwise false
 */
const pdpIsLoaded = () => {
  return document.querySelector(selector.pdp.wrapper) || false;
};

/**
 * Retrieves the product ID from the product detail page
 * @returns {string} - Returns the product ID if found, otherwise an empty string
 */
const getProductIdFromPdp = () => {
  const pdpButtonAddToCart = document.querySelector(
    selector.pdp.buttonAddToCart
  );
  const productId = pdpButtonAddToCart?.dataset?.pid || '';

  return productId;
};

/**
 * Launches a PDP view event if the product ID is available
 * @returns {void}
 */
const launchPdpViewEvent = () => {
  const productId = getProductIdFromPdp();

  if (productId) {
    const event = {
      name: eventName.pdpView,
      sku: productId
    };

    addEventToLocalStorage(event);
  }
};

/**
 * Launches an add to cart event with the given product ID
 * @param {string} productId - The ID of the product being added to the cart
 * @returns {void}
 */
const launchAddToCartEvent = (productId) => {
  const event = {
    name: eventName.addToCart,
    sku: productId
  };
  addEventToLocalStorage(event);
};

/**
 * Listens for clicks on product variant tiles and saves the event to local storage
 * @returns {void}
 */
const listenForProductVariantClick = () => {
  const productTileVariants =
    document.querySelectorAll(selector.productTileVariant) || [];

  productTileVariants.forEach((variant) => {
    const productId = variant?.dataset?.pid || '';
    if (productId) {
      if (
        !pdpIsLoaded() &&
        !isViewed(productId, listOfProductTileVariantsViewed)
      ) {
        variant.addEventListener('click', () => {
          const event = {
            name: eventName.productVariantClick,
            sku: productId
          };
          addEventToLocalStorage(event);
        });

        listOfProductTileVariantsViewed.push(productId);
      }
    }
  });
};

/**
 * Determine if the element is already viewed
 * @param {HTMLElement} element - The element to check
 * @param {string} string - The string to check against the list
 * @returns {Boolean} - True if the element is already viewed, otherwise false
 */
const isViewed = (string, list) => list.includes(string);

/**
 * Calculates the score based on events stored in local storage
 * @returns {number} - Returns the total score based on the events
 */
const getScore = () => {
  const eventsList = getEventsListFromLocalStorage();
  let score = 0;

  eventsList.forEach((event) => {
    const currentEventName = event.name || '';

    if (currentEventName === eventName.pdpView) {
      score += eventScore.pdpView;
    } else if (currentEventName === eventName.addToCart) {
      score += eventScore.addToCart;
    } else if (currentEventName === eventName.productVariantClick) {
      score += eventScore.productVariantClick;
    }
  });

  return score;
};

/**
 * Checks local storage for events and triggers the experiment if conditions are met. Also trims the list if it exceeds the maximum limit.
 * @returns {void}
 */
const checkLocalStorageForEvents = () => {
  const eventsList = getEventsListFromLocalStorage();

  launchExperiment();

  // If the number of events exceeds the maximum limit, trim the list
  if (eventsList.length > maxEventsToStore) {
    eventsList.splice(0, eventsList.length - maxEventsToStore);
    localStorage.setItem(localStorageName.events, JSON.stringify(eventsList));
  }
};

/**
 * Checks if the user has accepted cookies for preferences
 * @returns {boolean} - Returns true if the user has accepted cookies for preferences, otherwise false
 */
const cookiesAccepted = () => {
  if (typeof Cookiebot !== 'undefined') {
    if (Cookiebot.consent && Cookiebot.consent.preferences) {
      // Check if the user has given consent for preferences cookies
      return Cookiebot.consent.preferences;
    }
  }

  return false; // Cookiebot is not loaded, cannot check consent
};

/**
 * Launches the experiment if the conditions are met:
 * - The customer belongs to the specified segment
 * - The score is above the defined threshold
 * @returns {void}
 */
const launchExperiment = () => {
  const score = getScore();

  if (
    cookiesAccepted() &&
    customerBelongsToSegment() &&
    score >= scoreThreshold
  ) {
    showButtonLink();
    // showPopup();
  }
};

/**
 * Creates a button link element with the specified properties
 * @returns {HTMLElement} - Returns the created button link element
 */
const createButtonLink = () => {
  const buttonLink = createElement({
    tag: 'a',
    content: getText(text.buttonLinkHeader),
    classes: [
      'tw-bg-gradient-to-tr',
      'tw-from-[#A56DF2]',
      'tw-via-[#A56DF2]',
      'tw-to-[#4B94F2]',
      'tw-text-white',
      'tw-ml-2',
      'tw-px-3',
      'tw-py-1',
      'tw-text-xs',
      'tw-font-bold',
      'tw-leading-tight',
      'tw-rounded-md',
      'tw-self-center',
      'tw-uppercase',
      selector.buttonLinkHeader.replace('.', '')
    ],
    attributes: [
      {
        name: 'href',
        value: linkPlpRelevanceRecommender
      }
    ]
  });

  return buttonLink;
};

/**
 * Creates an icon link element with the specified properties
 * @returns {HTMLElement} - Returns the created icon link element
 */
const createIconLink = () => {
  const iconLink = createElement({
    tag: 'a',
    content: starsIcon,
    classes: [
      'tw-w-5',
      'tw-h-5',
      'tw-inline-block',
      'tw-text-[#F7D3FF]',
      'mouse:md:tw-hidden',
      selector.iconLinkHeader.replace('.', '')
    ],
    attributes: [
      {
        name: 'href',
        value: linkPlpRelevanceRecommender
      }
    ]
  });

  return iconLink;
};

/**
 * Appends the button link to the desktop menu if it exists
 * @returns {void}
 */
const showButtonLink = () => {
  const existingButtonLink =
    document.querySelector(selector.buttonLinkHeader) || null;
  const existingIconLink =
    document.querySelector(selector.iconLinkHeader) || null;

  if (!existingButtonLink) {
    const buttonLink = createButtonLink();

    if (element.menuDesktop) {
      appendElementContent(element.menuDesktop, buttonLink);
    }
  }

  if (!existingIconLink) {
    const iconLink = createIconLink();
    const iconsWrapper = element.cartIcon?.parentElement || null;
    if (iconsWrapper) {
      prepend(iconsWrapper, iconLink);
    }
  }
};

/**
 * Displays a popup if it has not been closed previously
 * @returns {void}
 */
const showPopup = () => {
  const popupClosed = getPopupClosedFromLocalStorage();

  if (!popupOpened && popupClosed !== localStorageValueForPopupClosed) {
    const popup = createPopup();

    setHtmlUnscrollable();
    appendElementContent(document.body, popup);

    const popupClose = document.querySelector(selector.popup.close) || null;
    if (popupClose) {
      listenDOMEvent(popupClose, 'click', closePopup);
    }

    const popupBackdrop =
      document.querySelector(selector.popup.backdrop) || null;
    if (popupBackdrop) {
      listenDOMEvent(popupBackdrop, 'click', function (event) {
        if (event.target === event.currentTarget) {
          closePopup();
        }
      });
    }

    const popupButton = document.querySelector(selector.popup.button) || null;
    if (popupButton) {
      listenDOMEvent(popupButton, 'click', closePopup);
    }

    popupOpened = true;

    pushOnDataLayer({
      event: 'recommendations_popup'
    });
  }
};

/**
 * Closes the popup and updates local storage to indicate it has been closed
 * @returns {void}
 */
const closePopup = () => {
  const popupBackdrop = document.querySelector(selector.popup.backdrop) || null;

  if (popupBackdrop) {
    setHtmlScrollable();
    localStorage.setItem(
      localStorageName.popupClosed,
      localStorageValueForPopupClosed
    );
    document.body.removeChild(popupBackdrop);
  }
};

/**
 * Creates a popup element
 * @returns {HTMLElement} - Returns the created popup element
 */
const createPopup = () => {
  const popupBackdrop = createElement({
    tag: 'div',
    classes: [
      selector.popup.backdrop.replace('.', ''),
      'tw-fixed',
      'tw-inset-0',
      'tw-z-999',
      'tw-w-svw',
      'tw-h-svh',
      'tw-bg-black',
      'tw-bg-opacity-75',
      'tw-grid',
      'tw-place-items-center',
      'tw-overflow-y-auto'
    ]
  });
  const popupImage = createElement({
    tag: 'img',
    classes: ['tw-w-full', 'tw-max-w-[600px]', 'tw-block'],
    attributes: [
      {
        name: 'src',
        value: backgroundImageRelevanceRecommenderPopup // Comes from content asset variable
      }
    ]
  });
  const popupTitle = createElement({
    tag: 'p',
    content: getText(text.popup.title),
    classes: [
      'tw-font-bold',
      'tw-font-sm',
      'xs:tw-text-base',
      'mobileLandscape:tw-text-lg'
    ]
  });
  const popupDescription = createElement({
    tag: 'p',
    classes: ['tw-text-xs', 'xs:tw-text-sm', 'mobileLandscape:tw-text-base'],
    content: getText(text.popup.description)
  });
  const popupButton = createElement({
    tag: 'a',
    content: getText(text.popup.button),
    classes: [
      'hw-button',
      'hw-button--primary-light',
      'tw-cursor-pointer',
      'tw-text-xs',
      'tw-p-2',
      'xs:tw-text-sm',
      'mobileLandscape:tw-py-3',
      'mobileLandscape:tw-text-base',
      selector.popup.button.replace('.', '')
    ],
    attributes: [
      {
        name: 'href',
        value: linkPlpRelevanceRecommender // Comes from content asset variable
      }
    ]
  });
  const popupTexts = createElement({
    tag: 'div',
    classes: [
      'tw-absolute',
      'tw-bottom-4',
      'tw-right-4',
      'tw-w-1/2',
      'tw-h-[60vw]',
      'tw-max-h-[400px]',
      'tw-p-2',
      'tw-flex',
      'tw-flex-col',
      'tw-gap-4',
      'tw-justify-center',
      'tw-leading-tight',
      'xs:tw-h-[65vw]',
      'xs:tw-gap-6',
      'xs:tw-p-4'
    ]
  });
  const popupClose = createElement({
    tag: 'div',
    content: '&#10005;',
    classes: [
      'tw-w-8',
      'tw-h-8',
      'tw-inline-grid',
      'tw-place-content-center',
      'tw-bg-white',
      'tw-text-text',
      'tw-text-2xl',
      'tw-cursor-pointer',
      'tw-rounded-full',
      'tw-font-thin',
      'sm:tw-w-10',
      'sm:tw-h-10',
      'hover:tw-bg-text',
      'hover:tw-text-white',
      'tw-transition-colors',
      selector.popup.close.replace('.', '')
    ]
  });
  const popupContent = createElement({
    tag: 'div',
    classes: ['tw-relative', 'tw-p-4', 'tw-space-y-4', 'tw-text-center']
  });
  popupTexts.appendChild(popupTitle);
  popupTexts.appendChild(popupDescription);
  popupTexts.appendChild(popupButton);
  popupContent.appendChild(popupClose);
  popupContent.appendChild(popupImage);
  popupContent.appendChild(popupTexts);
  popupBackdrop.appendChild(popupContent);

  return popupBackdrop;
};

/**
 * Retrieves the language from the locale cookie
 * @returns {string} - Returns the language code from the cookie, or 'en'
 */
const getLanguageFromCookie = () => {
  const localeCookieValue = getCookie(localeCookieName);
  return localeCookieValue ? localeCookieValue.split('_')[0] : 'en';
};

/**
 * Retrieves the text for a given key based on the language from the cookie
 * @param {object} textObject - An object containing text in different languages
 * @returns {string} - Returns the text in the language specified by the cookie, or an empty string if not found
 */
const getText = (textObject) => {
  const language = getLanguageFromCookie();
  return textObject[language] || '';
};

// Initialize the script when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', function () {
  // Listen for product detail page view
  if (pdpIsLoaded()) {
    launchPdpViewEvent();
  }

  // Launch the experiment if conditions are met
  launchExperiment();

  // Listen for product variant clicks
  listenForProductVariantClick();
});

// Listen for updates to the product detail page content
document.addEventListener('afterUpdateContentOfProductDetailPage', function () {
  launchPdpViewEvent();
});

// Listen for add to cart
document.addEventListener(gtmCustomEvent.afterAddToCart, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;

  if (productId) {
    launchAddToCartEvent(productId);
  }
});

// Listen for product tiles loaded
document.addEventListener(customEvent.listenProductTiles, () => {
  listenForProductVariantClick();
});

// Listen for Cookiebot consent ready event
// This is to ensure that the experiment only runs when the user has given consent for preferences cookies
document.addEventListener(customEvent.cookieBot.onConsentReady, () => {
  launchExperiment();
});

// Listen for local storage changes in other tabs
window.addEventListener('storage', (event) => {
  if (event.key === localStorageName.events) {
    checkLocalStorageForEvents();
  }
  if (event.key === localStorageName.popupClosed) {
    const popupClosed = getPopupClosedFromLocalStorage();
    if (popupClosed === localStorageValueForPopupClosed) {
      closePopup();
    }
  }
});
